import { useDashboardSurveyList, type SurveyListRow } from "./useDashboardSurveyList";
import { Survey, SurveyStatus } from "../../../redux/survey/survey.types";
import DashboardSurveyToolbar from "./dashboard-survey-toolbar";
import DashboardSurveyPagination from "./dashboard-survey-pagination.component";
import DashboardSurveyListEmpty from "./dashboard-survey-list-empty.component";
import SurveyCard from "./survey-card.component";
import SurveyGroupCard from "./survey-group-card.component";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Button } from "src/components/ui/button";
import { Bot, Hammer, LayoutGrid, Link2, MessageCircleQuestion, Plus, Table as TableIcon } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { canEditSurvey } from "../../../redux/survey/survey.helpers";
import SurveyCardMenu from "./survey-card-menu.component";
import SurveyGroupTableMenu from "./survey-group-table-menu.component";
import { Skeleton } from "src/components/ui/skeleton";
import useCreateNewSurvey from "src/hooks/useCreateNewSurvey";
import { hasSurveyCreateAccess } from "src/services/permission/permission.helpers";
import { useSelector } from "react-redux";
import { getUserStateActiveClient } from "src/redux/user/user.selectors";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import AISurveyCreationModal from "./ai-survey-creation-modal";
import type { SurveyGroup } from "src/types/survey-group.types";
import { buildSurveyGroupResultsPath } from "../utils/survey-group-results-navigation";

interface DashboardSurveyTableProps {
  onSurveyAction: (survey: Survey, action: 'assign-client') => void;
}

function groupQuestionLinkCount(g: SurveyGroup): number {
  if (typeof g.questionLinkCount === 'number') {
    return g.questionLinkCount;
  }
  return g.mappings.reduce((acc, m) => acc + m.linkedQuestions.length, 0);
}

function groupStartedTotal(g: SurveyGroup): number | undefined {
  if (!g.started) {
    return undefined;
  }
  return g.started.human + g.started.ai;
}

function groupCompletedTotal(g: SurveyGroup): number | undefined {
  if (!g.completed) {
    return undefined;
  }
  return g.completed.human + g.completed.ai;
}

const DashboardSurveyTable = ({ onSurveyAction }: DashboardSurveyTableProps) => {
  const navigate = useNavigate();
  const { refetch, listRows, isListEmpty } = useDashboardSurveyList();
  const [isTableView, setIsTableView] = useState(true);
  const { createNewSurvey } = useCreateNewSurvey();
  const { clientId } = useParams<{ clientId?: string }>();
  const client = useSelector(getUserStateActiveClient);
  const [isAISurveyCreationModalOpen, setIsAISurveyCreationModalOpen] = useState(false);

  const getStatusColor = (status: SurveyStatus, isDot: boolean = false) => {
    switch (status) {
      case SurveyStatus.Draft:
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case SurveyStatus.Live:
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      case SurveyStatus.Complete:
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const canCreateSurvey = clientId || client ? hasSurveyCreateAccess({ permissions: client?.permissions }) : true;

  const goGroupResults = (g: SurveyGroup) => {
    navigate(buildSurveyGroupResultsPath(g, clientId));
  };

  const handleSurveyRowClick = (survey: Survey | undefined) => {
    if (!survey) {
      return;
    }

    if (canEditSurvey(survey.surveyStatus)) {
      if (clientId) {
        navigate(`/client/${clientId}/surveys/create?ext-survey-id=${survey.extSurveyId}`);
      } else {
        navigate(`/surveys/create?ext-survey-id=${survey.extSurveyId}`);
      }
    } else {
      if (clientId) {
        navigate(`/client/${clientId}/results/overview/${survey.extSurveyId}`);
      } else {
        navigate(`/results/overview/${survey.extSurveyId}`);
      }
    }
  };

  const handleCreateNewSurvey = () => {
    void createNewSurvey();
    void refetch();
  };

  const renderTable = (rows: SurveyListRow[]) => {
    return (
      <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex justify-between bg-gray-201">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2 p-2 pl-4 items-center font-medium">
              <MessageCircleQuestion className="w-4 h-4" />
              Surveys &amp; groups
            </div>
          </div>
        </div>
        <Table>
          <TableHeader className="p-0 text-sm border-b border-gray-201">
            <TableRow className="border-none">
              <TableHead className="w-[88px] max-w-[88px] h-8 text-gray-600">
                Type
              </TableHead>
              <TableHead className="text-gray-600 h-8">
                Title
              </TableHead>
              <TableHead className="text-gray-600 h-8">
                Created
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Questions
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Starts
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Completes
              </TableHead>
              <TableHead className="w-[30px] h-8" />
            </TableRow>
          </TableHeader>
          <TableBody className="rounded-none">
            {rows.map((row, index) => {
              const stripe = index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]';
              if (row.kind === 'group') {
                const g = row.group;
                const groupStarts = g ? groupStartedTotal(g) : undefined;
                const groupCompletes = g ? groupCompletedTotal(g) : undefined;
                return (
                  <TableRow
                    key={g ? `group-row-${g.id}` : `group-skel-${index}`}
                    onClick={() => g && goGroupResults(g)}
                    className={`hover:bg-violet-50/80 transition-colors border-b border-gray-100 last:border-b-0 ${stripe} cursor-pointer`}
                  >
                    <TableCell className="text-center py-1.5">
                      {g ? (
                        <span className="max-w-[88px] inline-flex items-center justify-center gap-1 py-1 px-2 text-xs rounded-full font-bold bg-violet-100 text-violet-900 shadow-sm">
                          <Link2 className="h-3 w-3 shrink-0" />
                          Group
                        </span>
                      ) : (
                        <Skeleton className="w-16 h-[26px] mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium py-1.5">
                      {g ? g.name : <Skeleton className="w-48 h-4" />}
                    </TableCell>
                    <TableCell className="py-1.5">
                      {g ? format(new Date(g.createdAt), 'MMM d, yyyy') : <Skeleton className="w-24 h-4" />}
                    </TableCell>
                    <TableCell className="text-center py-1.5 text-sm">
                      {g ? groupQuestionLinkCount(g) : <Skeleton className="w-8 h-4 mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center py-1.5 text-sm">
                      {g ? (
                        typeof groupStarts === 'number' ? (
                          <span className="text-sm">
                            {groupStarts}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )
                      ) : (
                        <Skeleton className="w-8 h-4 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center py-1.5 text-sm">
                      {g ? (
                        typeof groupCompletes === 'number' ? (
                          <span className="text-sm">
                            {groupCompletes}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            —
                          </span>
                        )
                      ) : (
                        <Skeleton className="w-8 h-4 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell
                      className="py-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {g ? <SurveyGroupTableMenu group={g} /> : <Skeleton className="w-6 h-6 mx-auto" />}
                    </TableCell>
                  </TableRow>
                );
              }

              const survey = row.survey;
              return (
                <TableRow
                  key={survey ? `survey-row-${survey.extSurveyId}` : `survey-skel-${index}`}
                  onClick={() => handleSurveyRowClick(survey)}
                  className={`hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${stripe} cursor-pointer`}
                >
                  <TableCell className="text-center py-1.5">
                    {survey ? (
                      <span
                        className={`max-w-[88px] inline-flex items-center justify-center py-1 px-2 text-xs rounded-full font-bold bg-white shadow-sm ${getStatusColor(survey.surveyStatus)}`}
                      >
                        {survey.surveyStatus !== SurveyStatus.Draft && (
                          <div className={`size-1 rounded-full mr-1 ${getStatusColor(survey.surveyStatus, true)}`} />
                        )}
                        {survey.surveyStatus.charAt(0).toUpperCase() + survey.surveyStatus.slice(1)}
                      </span>
                    ) : (
                      <Skeleton className="w-16 h-[26px] mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium py-1.5">
                    {survey ? survey.title : <Skeleton className="w-48 h-4" />}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {survey ? format(new Date(survey.createdAt), 'MMM d, yyyy') : <Skeleton className="w-24 h-4" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? survey.questionCount : <Skeleton className="w-8 h-4 mx-auto" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? (
                      (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                        <span className="text-sm">
                          {survey.started.human + survey.started.ai}
                        </span>
                      ) : (
                        <span className="text-sm">
                          -
                        </span>
                      )
                    ) : <Skeleton className="w-8 h-4 mx-auto" />}
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    {survey ? (
                      (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                        <span className="text-sm">
                          {survey.completed.human + survey.completed.ai}
                        </span>
                      ) : (
                        <span className="text-sm">
                          -
                        </span>
                      )
                    ) : <Skeleton className="w-8 h-4 mx-auto" />}
                  </TableCell>
                  <TableCell
                    className="py-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {survey ? (
                      <SurveyCardMenu
                        survey={survey}
                        onSurveyAction={onSurveyAction}
                      />
                    ) : <Skeleton className="w-6 h-6 mx-auto" />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderCards = (rows: SurveyListRow[]) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
        {rows.map((row, index) => {
          if (row.kind === 'group') {
            return (
              <SurveyGroupCard
                key={row.group ? `g-card-${row.group.id}` : `g-card-${index}`}
                group={row.group}
                index={index}
                onOpen={row.kind === 'group' && row.group ? () => goGroupResults(row.group) : undefined}
              />
            );
          }
          return (
            <SurveyCard
              survey={row.survey}
              key={row.survey?.extSurveyId ?? `s-card-${index}`}
              index={index}
              onSurveyAction={onSurveyAction}
            />
          );
        })}
      </div>
    );
  };

  const showData = isTableView ? renderTable(listRows) : renderCards(listRows);

  return (
    <div
      className="flex flex-col gap-4 grow"
      id="survey-list"
    >
      <div className="flex items-center justify-end gap-4">
        <div className="flex-1">
          <DashboardSurveyToolbar />
        </div>
        <div className="flex items-center gap-1 p-1">
          <Button
            variant={isTableView ? "primary" : "ghost"}
            size="med"
            className={`${!isTableView ? "text-black/60" : ""}`}
            onClick={() => setIsTableView(true)}
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={!isTableView ? "primary" : "ghost"}
            size="med"
            className={`${isTableView ? "text-black/60" : ""} hover:bg-blue-500 hover:text-white hover:opacity-75`}
            onClick={() => setIsTableView(false)}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
        {canCreateSurvey && (
          <>
            <div className="h-8 border-l-1 border-black/10 -mx-1 hidden lg:block" />
            <div className="flex items-center gap-1 p-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-gray-201 text-black hover:bg-blue-500 hover:text-white"
                    variant="gray"
                    size="med"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={handleCreateNewSurvey}
                  >
                    <div className="flex items-center gap-2">
                      <Hammer className="w-4 h-4" />
                      Build from Scratch
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setIsAISurveyCreationModalOpen(true)}
                  >
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      Generate with AI
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>
      {isListEmpty ? <DashboardSurveyListEmpty /> : showData}
      <DashboardSurveyPagination />

      <AISurveyCreationModal
        isOpen={isAISurveyCreationModalOpen}
        onClose={() => setIsAISurveyCreationModalOpen(false)}
      />
    </div>
  );
};

export default DashboardSurveyTable;
